<script setup lang="tsx">
import { ref, watch } from 'vue'
import Autocomplete from '../widgets/Autocomplete.vue'
import type { Asset, BusinessProcess, DataAsset, DataAssetWithConnections, Response } from '@/models/api-generated'
import { genRouteID } from '@/services/utils'
import CancelSaveButtons from '../widgets/CancelSaveButtons.vue'
import sliderStore from '@/stores/slider'
import toastStore from '@/stores/toast'
import http from '@/services/http'
import { dataAssetRefreshCallback } from '@/components/business_processes/shared'
import { useI18n } from 'vue-i18n'
import { TagAsset, TagBP } from '@/models/enums-generated'

const { t } = useI18n()
const slider = sliderStore()
const toaster = toastStore()
const props = defineProps<{ canEdit: boolean; isChangeMode: boolean }>()
const model = defineModel<DataAssetWithConnections>({ required: true })
const selectedAsset = ref<Asset>()
const selectedBP = ref<BusinessProcess>()
const isConnectionEdit = ref(false)
const canSave = ref(true)
const emit = defineEmits(['cancel-edit'])

const editBtns = (
	<CancelSaveButtons saveId="save-data-asset" cancelId="cancel-data-asset" canSave={canSave} onCancel={cancel} onSave={save} />
)

watch(selectedAsset, (newAsset) => {
	if (newAsset && !model.value.assets.some((a) => a.id === newAsset.id)) {
		model.value.assets.push(newAsset)
		selectedAsset.value = undefined
	}
})
watch(selectedBP, (newBP) => {
	if (newBP && !model.value.businessProcesses.some((bp) => bp.id === newBP.id)) {
		model.value.businessProcesses.push(newBP)
		selectedBP.value = undefined
	}
})

async function save() {
	if (!model.value) return
	const data = {
		bpIds: model.value.businessProcesses.map((a) => a.id),
		assetIds: model.value.assets.map((a) => a.id),
	}

	const url = `/api/addDataAssetConnections/${genRouteID(model.value.dataAsset.id, '', 'DataAsset')}`

	const { status } = await http.post<Response<DataAsset>>(url, data)
	if (status === 200) {
		toaster.showSuccess(t('misc.successfullyEdited'))
		slider.setUnsavedChanges(false)
		slider.close()
		dataAssetRefreshCallback.value?.forEach((fn) => fn())
	}
}

async function cancel() {
	isConnectionEdit.value = false
	emit('cancel-edit')
}

function removeConnection(type: string, id: number) {
	if (type === TagAsset) {
		model.value.assets = model.value.assets.filter((a) => a.id !== id)
	}
	if (type === TagBP) {
		model.value.businessProcesses = model.value.businessProcesses.filter((bp) => bp.id !== id)
	}
}

function setMode() {
	isConnectionEdit.value = !isConnectionEdit.value
	slider.setFooter(editBtns)
}

function goToAssetPage(name: string) {
	const params = new URLSearchParams()
	params.append(`name`, name)
	window.location.href = `/app/assets?${params}`
}
</script>

<template>
	<template v-if="model">
		<div v-if="!props.isChangeMode" class="pt-4 d-flex flex-sm-column flex-content-contain">
			<div class="row">
				<h6 class="col mt-2">{{ $t('connections') }}</h6>
				<div v-if="!isChangeMode && props.canEdit && !isConnectionEdit" class="col-8 d-flex justify-content-end mb-1">
					<button data-test="add-dataasset-connection" type="button" class="btn btn-outline-primary btn-sm" @click="setMode()">
						<i class="bi bi-link-45deg me-1"></i> {{ $t('asset.editConnections') }}
					</button>
				</div>
			</div>

			<hr class="mt-0" />

			<div v-if="isConnectionEdit" class="row mt-3">
				<div class="col-4">
					<label for="asset-autocomplete">{{ $t('chooseAsset') }}</label>
				</div>
				<div class="col-8 d-flex justify-content-end">
					<Autocomplete
						v-model="selectedAsset"
						id="asset-select"
						:url="'/api/dataassetconnections'"
						:label="$t('misc.search')"
						:type="TagAsset"
						:instance-id="model.dataAsset.instance.id"
						:exlude-ids="model.assets.map((a) => a.id)"
					/>
				</div>
			</div>

			<div v-if="isConnectionEdit" class="row mt-3">
				<div class="col-4">
					<label for="bp-autocomplete">{{ $t('chooseBusinessProcess') }}</label>
				</div>
				<div class="col-8 d-flex justify-content-end">
					<Autocomplete
						v-model="selectedBP"
						id="bp-select"
						:url="'/api/dataassetconnections'"
						:label="$t('misc.search')"
						:type="TagBP"
						:instance-id="model.dataAsset.instance.id"
						:exlude-ids="model.businessProcesses.map((bp) => bp.id)"
					/>
				</div>
			</div>

			<div v-for="process in model.businessProcesses" :key="process.id" class="row mt-3">
				<div class="col-4">{{ process.name }}</div>
				<div class="col-8 d-flex justify-content-end">
					<a v-one-tooltip="process.name" :href="`./app/bp/view/${genRouteID(process.id, '', 'BusinessProcess')}`" class="me-2">
						<i class="bi bi-info-circle-fill"></i>
					</a>
					<span v-one-tooltip="$t('businessProcess')" class="badge bg-primary me-2 fixed-badge">BP</span>
					<button
						v-if="isConnectionEdit"
						@click="removeConnection('businessProcess', process.id)"
						class="btn-close"
						v-one-tooltip="$t('misc.remove')"
					></button>
				</div>
			</div>

			<div v-for="a in model.assets" :key="a.id" class="row mt-3">
				<div class="col-4">{{ a.name }}</div>
				<div class="col-8 d-flex justify-content-end">
					<a v-one-tooltip="a.name" class="me-2">
						<i class="bi bi-info-circle-fill" @click="goToAssetPage(a.name)"></i>
					</a>
					<span v-one-tooltip="$t('asset.label')" class="badge bg-primary me-2 fixed-badge">A</span>
					<button
						v-if="isConnectionEdit"
						@click="removeConnection('asset', a.id)"
						class="btn-close"
						v-one-tooltip="$t('misc.remove')"
					></button>
				</div>
			</div>
		</div>
	</template>
</template>

<style lang="sass" scoped>
.fixed-badge
  max-height: 1.5rem
</style>
